import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClerk, useUser } from "@clerk/clerk-react";
import {
  FiLogOut,
  FiMenu,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaPaintBrush, FaHome, FaImage, FaVideo, FaFolder, FaCreditCard } from "react-icons/fa";


// Define nav items array
const navItems = [
  { path: 'home', label: 'Home', icon: FaHome },
  { path: 'image', label: 'Apparel Images', icon: FaImage },
  { path: 'video', label: 'Apparel Videos', icon: FaVideo },
  { path: 'studio', label: 'Prototype Studio', icon: FaPaintBrush },
  { path: 'dashboard', label: 'Dashboard', icon: FaFolder },
  { path: 'billing', label: 'Billing', icon: FaCreditCard },
  // { path: 'settings', label: 'Settings', icon: FiSettings },
];

const NavBar = ({ activeTab, setActiveTab, handleUserAction, onCollapseChange }) => {
  const { signOut } = useClerk();
  const { user } = useUser();
  const [avatarError, setAvatarError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isCollapsed, setIsCollapsed] = useState(true); // Collapsed by default
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
      if (window.innerWidth > 1024) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Notify parent component about collapse state changes
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);

  const handleTabChange = (path) => {
    // Close the nav immediately on mobile for better UX before navigating
    if (isMobile) {
      setIsOpen(false);
    }
    handleUserAction(() => {
      setActiveTab(path);
      navigate(`/${path}`);
    });
  };

  const handleSignOut = () => {
    handleUserAction(() => {
      sessionStorage.clear();
      signOut();
      if (isMobile) {
        setIsOpen(false);
      }
    });
  };

  const toggleNav = () => {
    setIsOpen(!isOpen);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <button 
        className="lg:hidden fixed top-4 left-4 z-[70] p-1.5 glass-effect backdrop-blur-xl bg-black/20 text-white rounded-lg hover:bg-white/10 transition-all duration-300 border border-white/10"
        onClick={toggleNav}
        aria-label="Toggle navigation menu"
      >
        {isOpen ? <FiX size={20} /> : <FiMenu size={20} />}
      </button>

      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[55] lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <nav className={`fixed top-0 left-0 h-screen ${
        isCollapsed && !isMobile ? 'w-12' : isMobile ? 'w-64' : 'w-48'
      } glass-effect backdrop-blur-2xl bg-black/10 border-r border-white/10 ${
        isCollapsed && !isMobile ? 'p-2' : isMobile ? 'p-4' : 'p-3'
      } flex flex-col transition-all duration-300 ease-in-out z-[60] ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 ${isMobile ? 'max-w-xs' : ''}`}>
        
        {/* Logo and Collapse Toggle */}
        <div className={`flex items-center ${
          isCollapsed && !isMobile ? 'flex-col gap-2 mb-3' : isMobile ? 'justify-between mb-5' : 'justify-between mb-4'
        }`}>
          <Link 
            to="/home" 
            className={`flex items-center cursor-pointer hover:opacity-80 transition-opacity ${
              isCollapsed && !isMobile ? 'justify-center' : 'gap-2'
            }`}
            onClick={() => handleTabChange('home')}
          >
            <img 
              src="/logo.png" 
              alt="Taco Logo" 
              className={`w-7 h-7 flex-shrink-0 ${
                isCollapsed && !isMobile ? 'mx-auto' : ''
              }`}
            />
            {(!isCollapsed || isMobile) && (
              <h1 className={`font-bold ${isMobile ? 'text-xl' : 'text-lg'}`}>
                <span className="gradient-text">Taco</span>
              </h1>
            )}
          </Link>
          
          {/* Collapse Toggle Button (Desktop only) */}
          {!isMobile && (
            <button
              onClick={toggleCollapse}
              className={`p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300 ${
                isCollapsed ? 'mx-auto' : ''
              }`}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <FiChevronRight size={14} /> : <FiChevronLeft size={14} />}
            </button>
          )}
        </div>

        {/* Navigation links */}
        <div className="flex flex-col gap-1.5 flex-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={`/${item.path}`}
              className={`flex items-center ${
                isCollapsed && !isMobile ? 'justify-center px-1.5 py-2' : isMobile ? 'px-3 py-2.5' : 'px-2 py-2'
              } rounded-lg transition-all duration-300 hover:bg-white/10 ${
                activeTab === item.path 
                  ? "bg-white/10 text-white font-semibold border border-white/20" 
                  : "text-gray-300 hover:text-white"
              } relative group`}
              onClick={() => handleTabChange(item.path)}
              title={isCollapsed && !isMobile ? item.label : undefined}
            >
              <div className="relative">
                <item.icon className={`${isMobile ? 'text-lg' : 'text-base'} ${
                  isCollapsed && !isMobile ? '' : 'mr-2'
                }`} />
                
                {/* Tooltip for collapsed state - only show on icon hover */}
                {isCollapsed && !isMobile && (
                  <div className="absolute left-full ml-2 px-2 py-1 glass-effect backdrop-blur-xl bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 border border-white/20 pointer-events-none">
                    {item.label}
                  </div>
                )}
              </div>
              
              {(!isCollapsed || isMobile) && (
                <span className={`font-medium ${isMobile ? 'text-sm' : 'text-xs'}`}>{item.label}</span>
              )}
            </Link>
          ))}
        </div>

        {/* Logout button */}
        <div className="mt-auto mb-3">
          <button
            className={`flex items-center ${
              isCollapsed && !isMobile ? 'justify-center px-1.5 py-2' : isMobile ? 'px-3 py-2.5' : 'px-2 py-2'
            } rounded-lg w-full text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all duration-300 relative group`}
            onClick={handleSignOut}
            title={isCollapsed && !isMobile ? "Logout" : undefined}
          >
            <div className="relative">
              <FiLogOut className={`${isMobile ? 'text-lg' : 'text-base'} ${
                isCollapsed && !isMobile ? '' : 'mr-2'
              }`} />
              
              {/* Tooltip for collapsed state - only show on icon hover */}
              {isCollapsed && !isMobile && (
                <div className="absolute left-full ml-2 px-2 py-1 glass-effect backdrop-blur-xl bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 border border-white/20 pointer-events-none">
                  Logout
                </div>
              )}
            </div>
            
            {(!isCollapsed || isMobile) && (
              <span className={`font-medium ${isMobile ? 'text-sm' : 'text-xs'}`}>Logout</span>
            )}
          </button>
        </div>
      </nav>
      
      {/* Right glow effect - position dynamically based on collapsed state */}
      <div className={`fixed top-0 ${
        isCollapsed && !isMobile ? 'left-12' : isMobile ? 'left-64' : 'left-48'
      } h-screen w-px bg-gradient-to-b from-transparent via-cyan-500/30 to-transparent z-[59] hidden lg:block transition-all duration-300`}></div>
    </>
  );
};

export default NavBar; 